import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { fetchClusters, deleteCluster, duplicateCluster } from '../store/slices/clusterSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch, FiCopy, FiArchive, FiDatabase } from 'react-icons/fi';

const ClustersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clusters, loadingCluster, error } = useSelector((state: RootState) => state.clusters);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchClusters());
  }, [dispatch]);

  const handleDelete = (id: string, clusterId: string) => {
    dispatch(
      showModal({
        title: 'Confirm Deletion',
        content: `Are you sure you want to delete cluster "${clusterId}"? The cluster will be moved to the deleted clusters list.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          try {
            await dispatch(deleteCluster(id)).unwrap();
            
            dispatch(
              addNotification({
                type: 'success',
                message: `Cluster "${clusterId}" was successfully deleted.`,
              })
            );
          } catch (error: any) {
            dispatch(
              addNotification({
                type: 'error',
                title: 'Deletion Failed',
                message: error.message || 'Failed to delete cluster.',
              })
            );
          }
        },
      })
    );
  };

  const handleDuplicate = (id: string, clusterData: any) => {
    dispatch(
      showModal({
        title: 'Duplicate Cluster',
        content: (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter new Cluster ID for the duplicated cluster:
            </p>
            <div>
              <label htmlFor="newClusterId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                New Cluster ID
              </label>
              <input
                type="text"
                id="newClusterId"
                className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                placeholder={`${clusterData.clusterId}-copy`}
                defaultValue={`${clusterData.clusterId}-copy`}
              />
            </div>
          </div>
        ),
        confirmLabel: 'Duplicate',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          try {
            const newClusterId = (document.getElementById('newClusterId') as HTMLInputElement).value;
            
            if (!newClusterId) {
              throw new Error('Cluster ID is required');
            }
            
            await dispatch(duplicateCluster({
              id, 
              newData: {
                clusterId: newClusterId
              }
            })).unwrap();
            
            dispatch(
              addNotification({
                type: 'success',
                message: `Cluster "${clusterData.clusterId}" was successfully duplicated.`,
              })
            );
          } catch (error: any) {
            dispatch(
              addNotification({
                type: 'error',
                title: 'Duplication Failed',
                message: error.message || 'Failed to duplicate cluster.',
              })
            );
          }
        },
      })
    );
  };

  // Filter clusters based on search term
  const filteredClusters = clusters.filter((cluster) => {
    return cluster.clusterId.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Db Clusters</h1>
        <div className="mt-3 sm:mt-0 space-y-2 sm:space-y-0 sm:flex sm:space-x-3">
          <Link
            to="/clusters/create"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiPlus className="mr-2" />
            New Db Cluster
          </Link>
          <Link
            to="/clusters/deleted"
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiArchive className="mr-2" />
            Deleted Db Clusters
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white sm:text-sm"
            placeholder="Search clusters..."
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Clusters table */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Cluster ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Servers
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Username
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Authentication
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loadingCluster ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredClusters.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {searchTerm
                      ? 'No clusters match your search'
                      : 'No clusters found. Create your first cluster to get started.'}
                  </td>
                </tr>
              ) : (
                filteredClusters.map((cluster) => (
                  <tr key={cluster._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiDatabase className="flex-shrink-0 mr-2 h-5 w-5 text-gray-400" />
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {cluster.clusterId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.Servers.map((server, index) => (
                        <div key={index}>
                          {server.Host}:{server.Port}
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.UserName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.AuthenticationMechanism}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/clusters/${cluster._id}`)}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                          title="View details"
                        >
                          <FiEye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/clusters/${cluster._id}/edit`)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit cluster"
                        >
                          <FiEdit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(cluster._id, cluster)}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          title="Duplicate cluster"
                        >
                          <FiCopy className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(cluster._id, cluster.clusterId)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete cluster"
                        >
                          <FiTrash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClustersPage;